import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Percent } from "lucide-react";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList } from "recharts";
import { tooltipStyle } from "./oversiktConstants";
import { formatNumber, dec, num } from "@/lib/formatters";

const mill = (v) => (v == null ? "—" : `${formatNumber(Math.round(v / 1000))} mill. kr`);
const kr = (v) => (v == null ? "—" : `${formatNumber(Math.round(v))} kr`);
const pst = (v) => (v == null ? "—" : `${dec(v, 2)} %`);

const MAAL = [
  { key: "rente_pct", label: "Rente av lånegjelda", format: pst, testId: "rente", desimalar: 1, land: true },
  { key: "laanegjeld_per_innb", label: "Lånegjeld per innbyggjar", format: kr, testId: "gjeld", land: true },
  { key: "rente_og_avdrag_per_innb", label: "Renter og avdrag per innbyggjar", format: kr, testId: "sum", land: true },
];

const FinanskostnadCompareChart = ({ data, colors }) => {
  const rows = Object.values(data?.kommuner || {});
  if (rows.length === 0) return null;
  const land = data.land;

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-finanskostnad-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Percent className="w-5 h-5 text-[#103766]" />
          Finanskostnader {rows[0].year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Kva rentene kostar: renta kommunane faktisk betaler av lånegjelda, kor stor gjelda er per innbyggjar, og kva eitt prosentpoeng høgare
          rente ville koste. Stipla linje er landssnittet.
        </p>
      </CardHeader>
      <CardContent className="pt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {MAAL.map((m) => {
          const bars = rows.filter((r) => r.latest?.[m.key] != null).map((r) => ({ name: r.name, verdi: r.latest[m.key] }));
          if (bars.length === 0) return null;
          return (
            <div key={m.key}>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">{m.label}</p>
              <div className="h-[240px]" data-testid={`compare-finans-${m.testId}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bars} barCategoryGap="35%" margin={{ top: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                    <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
                    <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => num(v, m.desimalar || 0)} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [m.format(v), m.label]} />
                    {m.land && land?.[m.key] != null && (
                      <ReferenceLine
                        y={land[m.key]}
                        stroke="#4B647F"
                        strokeDasharray="4 4"
                        label={{ value: `Landet ${m.format(land[m.key])}`, position: "insideTopRight", fontSize: 11, fill: "#4B647F" }}
                      />
                    )}
                    <Bar dataKey="verdi" radius={[4, 4, 0, 0]}>
                      {bars.map((b, i) => <Cell key={b.name} fill={colors[i % colors.length]} />)}
                      <LabelList dataKey="verdi" position="top" formatter={(v) => num(v, m.desimalar || 0)} fontSize={12} fill="#1F2A36" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
        <div className="lg:col-span-3 overflow-x-auto">
          <table className="w-full text-xs" data-testid="compare-finans-table">
            <thead>
              <tr className="border-b border-[#E6E9F0] text-[#4B647F]">
                <th className="py-2 pr-3 text-left font-medium">Kommune</th>
                <th className="py-2 px-3 text-right font-medium">Lånegjeld</th>
                <th className="py-2 px-3 text-right font-medium">Renteutgifter</th>
                <th className="py-2 px-3 text-right font-medium">Rente av gjelda</th>
                <th className="py-2 px-3 text-right font-medium">Netto renter</th>
                <th className="py-2 px-3 text-right font-medium">Avdrag</th>
                <th className="py-2 px-3 text-right font-medium">Nedbetalingstid</th>
                <th className="py-2 px-3 text-right font-medium">Renter+avdrag av driftsinntekter</th>
                <th className="py-2 pl-3 text-right font-medium">+1 pp rente</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.name} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`compare-finans-row-${i}`}>
                  <td className="py-2 pr-3 font-medium" style={{ color: colors[i % colors.length] }}>{r.name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{mill(r.latest.laanegjeld)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{mill(r.latest.renteutgifter)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{pst(r.latest.rente_pct)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{pst(r.latest.netto_rente_pct)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{mill(r.latest.avdrag)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {r.latest.nedbetalingstid ? `${dec(r.latest.nedbetalingstid, 1)} år` : "—"}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{pst(r.latest.rente_og_avdrag_pct_driftsinntekter)}</td>
                  <td className="py-2 pl-3 text-right tabular-nums">
                    {r.latest.renteeffekt ? `${mill(r.latest.renteeffekt[0].kr)} (${kr(r.latest.renteeffekt[0].per_innb)}/innb.)` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[#8AA4BF] mt-2">
            Lånegjeld = langsiktig gjeld minus pensjonsforpliktingar, som ikkje ber renter. Renta er renteutgiftene delt på lånegjelda ved årsslutt
            (implisitt rentesats). Netto renter er etter renteinntekter og utbytte. Kjelder: SSB 13543 og 13545, kommunekassa.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default FinanskostnadCompareChart;
